import { Plus } from "lucide-react";
import { ymd, fmt, itemsForDay, todayStr } from "@/lib/dates";
import { initials } from "@/lib/colors";
import { ShiftChip, TaskChip, HolidayChip } from "@/components/calendar/Chips";

const Cell = ({ day, data, user, onSelect, onAdd }) => {
  const { shifts, tasks, holidays } = itemsForDay(data, day, user.id);
  return (
    <td className={`align-top p-1.5 border-l border-slate-100 cursor-copy hover:bg-slate-50 transition-colors ${day === todayStr() ? "bg-[#EAF2F2]/50" : ""}`} onClick={(e) => (e.target === e.currentTarget || e.target.dataset.cellfill) && onAdd(day, user.id)} data-testid={`team-cell-${user.id}-${day}`}>
      <div className="space-y-1 min-h-[48px]" data-cellfill="1">
        {holidays.map((h) => <HolidayChip key={h.id} holiday={h} showName={false} onClick={() => onSelect({ kind: "holiday", item: h })} />)}
        {shifts.map((s) => <ShiftChip key={s.id} shift={s} onClick={() => onSelect({ kind: "shift", item: s })} />)}
        {tasks.slice(0, 2).map((t) => <TaskChip key={t.id} task={t} compact onClick={() => onSelect({ kind: "task", item: t })} />)}
        {tasks.length > 2 && <div className="text-[11px] text-slate-500 px-1">+{tasks.length - 2} more tasks</div>}
      </div>
    </td>
  );
};

const AgendaDay = ({ day, data, onSelect, onAdd }) => {
  const rows = data.users.map((u) => ({ u, ...itemsForDay(data, day, u.id) })).filter((r) => r.shifts.length || r.tasks.length || r.holidays.length);
  const isToday = day === todayStr();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`team-agenda-day-${day}`}>
      <div className={`flex items-center justify-between px-4 py-2.5 ${isToday ? "bg-[#24474A] text-white" : "bg-slate-50 text-slate-700"}`}>
        <span className="font-bold">{fmt(day, "EEE d MMM")} <span className="font-normal opacity-70 text-xs">· {rows.filter((r) => r.shifts.length).length} on shift</span></span>
        <button onClick={() => onAdd(day)} className="w-8 h-8 rounded-full grid place-items-center hover:bg-black/5" data-testid={`team-agenda-add-${day}`}><Plus className="w-4 h-4" /></button>
      </div>
      {rows.map(({ u, shifts, tasks, holidays }) => (
        <div key={u.id} className="flex gap-3 px-4 py-2.5 border-t border-slate-100">
          <span className="w-8 h-8 rounded-full bg-[#EAF2F2] text-[#24474A] grid place-items-center text-xs font-bold shrink-0">{initials(u.name)}</span>
          <div className="flex-1 min-w-0 space-y-1">
            <div className="text-sm font-bold text-slate-800">{u.name}</div>
            {holidays.map((h) => <HolidayChip key={h.id} holiday={h} showName={false} onClick={() => onSelect({ kind: "holiday", item: h })} />)}
            {shifts.map((s) => <ShiftChip key={s.id} shift={s} onClick={() => onSelect({ kind: "shift", item: s })} />)}
            {tasks.map((t) => <TaskChip key={t.id} task={t} onClick={() => onSelect({ kind: "task", item: t })} />)}
          </div>
        </div>
      ))}
      {rows.length === 0 && <div className="px-4 py-4 text-sm text-slate-400 border-t border-slate-100">Nobody scheduled.</div>}
    </div>
  );
};

export const TeamGrid = ({ days, data, onSelect, onAdd }) => (
  <>
  <div className="md:hidden space-y-3" data-testid="team-agenda">
    {days.map((d) => <AgendaDay key={ymd(d)} day={ymd(d)} data={data} onSelect={onSelect} onAdd={onAdd} />)}
  </div>
  <div className="hidden md:block overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="team-calendar">
    <table className={`w-full table-fixed border-collapse ${days.length > 1 ? "min-w-[1100px]" : "min-w-[480px]"}`}>
      <thead>
        <tr className="bg-slate-50/70">
          <th className="sticky left-0 z-10 bg-slate-50 text-left px-4 py-3 text-[11px] uppercase tracking-[0.15em] text-slate-500 w-[200px]">Team member</th>
          {days.map((d) => (
            <th key={ymd(d)} className={`px-2 py-2 text-left border-l border-slate-100 ${ymd(d) === todayStr() ? "text-[#24474A]" : "text-slate-600"}`}>
              <div className="text-[11px] uppercase tracking-[0.15em]">{fmt(d, "EEE")}</div>
              <div className="text-lg font-bold">{fmt(d, "d MMM")}</div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.users.map((u) => (
          <tr key={u.id} className="border-t border-slate-100" data-testid={`team-row-${u.id}`}>
            <td className="sticky left-0 z-10 bg-white px-4 py-3 align-top">
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-full bg-[#EAF2F2] text-[#24474A] grid place-items-center text-xs font-bold shrink-0">{initials(u.name)}</span>
                <div className="leading-tight">
                  <div className="font-bold text-sm text-slate-800">{u.name}</div>
                  <div className="text-[11px] text-slate-500">{u.department}</div>
                </div>
              </div>
            </td>
            {days.map((d) => <Cell key={ymd(d)} day={ymd(d)} data={data} user={u} onSelect={onSelect} onAdd={onAdd} />)}
          </tr>
        ))}
        {data.users.length === 0 && (
          <tr><td colSpan={days.length + 1} className="text-center py-10 text-sm text-slate-500">No managers in this department.</td></tr>
        )}
      </tbody>
    </table>
  </div>
  </>
);
